import React, { useContext } from 'react';
import { render } from '@rc-component/util/lib/React/render';
import { AppConfigContext } from '../app/context';
import ConfigProvider, { ConfigContext, globalConfig, warnContext } from '../config-provider';
import PureList from './PureList';
import PurePanel from './PurePanel';
import useNotification, { useInternalNotification } from './useNotification';
let notification = null;
let act = (callback) => callback();
let taskQueue = [];
let defaultGlobalConfig = {};
function getGlobalContext() {
    const { getContainer, rtl, maxCount, top, bottom, showProgress, pauseOnHover } = defaultGlobalConfig;
    const mergedContainer = getContainer?.() || document.body;
    return {
        getContainer: () => mergedContainer,
        rtl,
        maxCount,
        top,
        bottom,
        showProgress,
        pauseOnHover,
    };
}
const GlobalHolder = React.forwardRef((props, ref) => {
    const { notificationConfig, sync } = props;
    const { getPrefixCls } = useContext(ConfigContext);
    const prefixCls = defaultGlobalConfig.prefixCls || getPrefixCls('notification');
    const appConfig = useContext(AppConfigContext);
    const [api, holder] = useInternalNotification({
        ...notificationConfig,
        prefixCls,
        ...appConfig.notification,
    });
    React.useEffect(sync, []);
    React.useImperativeHandle(ref, () => {
        const instance = { ...api };
        Object.keys(instance).forEach((method) => {
            instance[method] = (...args) => {
                sync();
                return api[method](...args);
            };
        });
        return {
            instance,
            sync,
        };
    });
    return holder;
});
const GlobalHolderWrapper = React.forwardRef((_, ref) => {
    const [notificationConfig, setNotificationConfig] = React.useState(getGlobalContext);
    const sync = () => {
        setNotificationConfig(getGlobalContext);
    };
    React.useEffect(sync, []);
    const global = globalConfig();
    const rootPrefixCls = global.getRootPrefixCls();
    const rootIconPrefixCls = global.getIconPrefixCls();
    const theme = global.getTheme();
    const dom = React.createElement(GlobalHolder, { ref: ref, sync: sync, notificationConfig: notificationConfig });
    return (React.createElement(ConfigProvider, { prefixCls: rootPrefixCls, iconPrefixCls: rootIconPrefixCls, theme: theme }, global.holderRender ? global.holderRender(dom) : dom));
});
const flushNotificationQueue = () => {
    if (!notification) {
        const holderFragment = document.createDocumentFragment();
        const newNotification = {
            fragment: holderFragment,
        };
        notification = newNotification;
        // Delay render to avoid sync issue
        act(() => {
            render(React.createElement(GlobalHolderWrapper, { ref: (node) => {
                    const { instance, sync } = node || {};
                    Promise.resolve().then(() => {
                        if (!newNotification.instance && instance) {
                            newNotification.instance = instance;
                            newNotification.sync = sync;
                            flushNotificationQueue();
                        }
                    });
                } }), holderFragment);
        });
        return;
    }
    // Notification not ready
    if (!notification.instance) {
        return;
    }
    // >>> Execute task
    taskQueue.forEach((task) => {
        switch (task.type) {
            case 'open': {
                act(() => {
                    notification.instance.open({
                        ...defaultGlobalConfig,
                        ...task.config,
                    });
                });
                break;
            }
            case 'destroy':
                act(() => {
                    notification?.instance?.destroy(task.key);
                });
                break;
        }
    });
    // Clean up
    taskQueue = [];
};
// ==============================================================================
// ==                                  Export                                  ==
// ==============================================================================
function setNotificationGlobalConfig(config) {
    defaultGlobalConfig = {
        ...defaultGlobalConfig,
        ...config,
    };
    // Trigger sync for it
    act(() => {
        notification?.sync?.();
    });
}
function open(config) {
    const global = globalConfig();
    if (process.env.NODE_ENV !== 'production' && !global.holderRender) {
        warnContext('notification');
    }
    taskQueue.push({ type: 'open', config });
    flushNotificationQueue();
}
const destroy = (key) => {
    taskQueue.push({ type: 'destroy', key });
    flushNotificationQueue();
};
const methods = ['success', 'info', 'warning', 'error'];
const baseStaticMethods = {
    open,
    destroy,
    config: setNotificationGlobalConfig,
    useNotification,
    _InternalPanelDoNotUseOrYouWillBeFired: PurePanel,
    _InternalListDoNotUseOrYouWillBeFired: PureList,
};
const staticMethods = baseStaticMethods;
methods.forEach((type) => {
    staticMethods[type] = (config) => open({ ...config, type });
});
// ==============================================================================
// ==                                   Test                                   ==
// ==============================================================================
const noop = () => { };
let _actWrapper = noop;
if (process.env.NODE_ENV === 'test') {
    _actWrapper = (wrapper) => {
        act = wrapper;
    };
}
const actWrapper = _actWrapper;
export { actWrapper };
let _actDestroy = noop;
if (process.env.NODE_ENV === 'test') {
    _actDestroy = () => {
        notification = null;
    };
}
const actDestroy = _actDestroy;
export { actDestroy };
export default staticMethods;
